import AssetTree from "@rio-cloud/rio-uikit/AssetTree";
import ErrorState from "@rio-cloud/rio-uikit/ErrorState";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import Tree, { SelectionChangeResponse } from "@rio-cloud/rio-uikit/Tree";
import TreeCategory from "@rio-cloud/rio-uikit/TreeCategory";
import isEmpty from "lodash/fp/isEmpty";
import isEqual from "lodash/fp/isEqual";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "../../configuration/setup/hooks";
import useQueryParams from "../../hooks/useQueryParams";
import { useGetAssetsQuery, useGetTagsQuery } from "../../services/truckApi";
import {
	addAssetsSelected,
	addTagsSelected,
	getAppData,
	setAllTrucksWithoutTag,
	setTrucksGlobal,
	setTrucksTagsGlobal,
} from "../../stores/app/appSlice";

const CATEGORY_TRUCKS = "trucks";
const defaultExpandedTruckTags: string[] = [];

export const getEmptyTagTrucks = (trucks: any) =>
	trucks?.reduce((acc: any, curr: any) => {
		return curr.groupIds[0] === "empty" && curr.groupIds.length === 1 ? [...acc, curr.id] : acc;
	}, []);

export const justHasEmptysTag = (assetsList: any, groups: any, emptyTagTrucks: any) =>
	isEqual(assetsList, emptyTagTrucks) && isEqual(groups, ["empty"]);

export const sanitizeAssetsList = (items: any) => {
	const assets = items.map((item: any) => {
		const { items } = item._embedded.tags;
		const groups = items.map((groups: any) => groups.id);
		const groupIds = isEmpty(groups) ? ["empty"] : groups;

		return { id: item.id, name: item.name, type: item.type, groupIds };
	});
	return assets;
};

export const createAssetsList = ({ groups, items, trucks }: any) =>
	groups.find((item: any) => item === "empty")
		? [
				...new Set([
					...items,
					...trucks
						.reduce((acc: any, curr: any) => {
							const assetId = curr.groupIds.find((group: any) => group === "empty") && curr.id;
							return acc.concat(assetId);
						}, [])
						.filter((item: any) => item !== undefined),
				]),
			]
		: items;

export const sanitizeTagsList = (items: any, intl: any) => {
	const tags = items.map((item: any) => ({ id: item.id, name: item.name }));
	tags.push({
		id: "empty",
		position: "last",
		name: intl.formatMessage({
			id: "general.app.sidebar.unassigned",
		}),
	});
	return tags;
};

const ApplicationSideBar = () => {
	const intl = useIntl();
	const dispatch = useAppDispatch();
	const [expandedTruckTags, setExpandedTruckTags] = useState<string[] | undefined>();
	const [currentCategoryId, setCurrentCategoryId] = useState(CATEGORY_TRUCKS);
	const { data: tags, error: errorTags, isLoading: loadingTags } = useGetTagsQuery();
	const { data: assets, error: errorAssets, isLoading: loadingAssets } = useGetAssetsQuery();
	const { assets: assetsGlobal, tags: tagsGlobal, screenType, allTags, allAssets } = useAppSelector(getAppData);
	const { getBooleanQueryParam, setBooleanQueryParam } = useQueryParams();

	useEffect(() => {
		async function initialData() {
			if (!loadingAssets && !isEmpty(assets)) {
				const sanitizedAssets = sanitizeAssetsList(assets);
				const assetsWithoutTag = sanitizedAssets.filter((asset: { groupIds: string | string[] }) => asset.groupIds.includes("empty"));
				const assetIdsWithoutTag = assetsWithoutTag.map((asset: { id: string }) => asset.id);
				dispatch(setAllTrucksWithoutTag(assetIdsWithoutTag));
				dispatch(setTrucksGlobal(sanitizedAssets));
			}

			if (!loadingTags && !isEmpty(tags)) {
				const assetTagIds = assets.flatMap((asset: any) => asset._embedded?.tags?.items?.map((item: any) => item.id) || []);
				const filteredTags = tags.filter((tag: any) => assetTagIds.includes(tag.id));
				dispatch(setTrucksTagsGlobal(sanitizeTagsList(filteredTags, intl)));
			}

			setExpandedTruckTags(defaultExpandedTruckTags);
		}
		initialData();
	}, [assets, errorAssets, errorTags, intl, tags]);

	const handleSelectTruck = ({ items, groups }: SelectionChangeResponse) => {
		dispatch(addTagsSelected(groups));

		if (groups.length == allTags.length) {
			dispatch(addAssetsSelected(allAssets.map((truck) => truck.id)));
		} else {
			dispatch(addAssetsSelected(items));
		}
	};

	const handleExpandTruckGroups = (expandedTruckGroups: any) => {
		setExpandedTruckTags(expandedTruckGroups);
	};

	const handleChangeCategories = (currentCategoryId: any) => {
		setCurrentCategoryId(currentCategoryId);
	};

	const [openAppTree, setOpenAppTree] = useState(true);

	return (
		<AssetTree
			onToggleTree={() => setOpenAppTree(!openAppTree)}
			onCategoryChange={handleChangeCategories}
			currentCategoryId={currentCategoryId}
			isOpen={openAppTree}
			maxWidth={450}
			fly={screenType.MOBILE_SCREEN || screenType.TABLET_SCREEN}
			minWidth={300}
		>
			<TreeCategory
				key={CATEGORY_TRUCKS}
				id={CATEGORY_TRUCKS}
				label={"Trucks"}
				icon={"rioglyph-truck"}
				hasSelection={!isEmpty(assetsGlobal)}
			>
				{loadingAssets && loadingTags && <Spinner />}
				{allTags && allAssets ? (
					<Tree
						items={allAssets}
						groups={allTags}
						hasMultiselect={true}
						selectedItems={assetsGlobal}
						expandedGroups={expandedTruckTags}
						onExpandGroupsChange={handleExpandTruckGroups}
						selectedGroups={tagsGlobal}
						onSelectionChange={handleSelectTruck}
						searchPlaceholder={intl.formatMessage({
							id: "general.app.sidebar.search",
						})}
					/>
				) : (
					!loadingAssets &&
					!loadingTags && (
						<div className="margin-y-auto">
							<ErrorState outerClassName="border-none" condensed headline="Something went wrong" />
						</div>
					)
				)}
			</TreeCategory>
		</AssetTree>
	);
};

export default ApplicationSideBar;
